/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Discover Endless Possibilities - accordion-style tabs with image display for Face Swap SEO page
 */
import Image from 'next/image';
import React, { useMemo, useState } from 'react';

import { useImagePreload } from '@/hooks/useImagePreload';

const ASSET_PREFIX = 'https://assets.falcocut.ai/face-swap/';

interface PossibilityItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

const possibilities: PossibilityItem[] = [
  {
    id: 'social-media',
    title: 'Social Media Trends & Memes',
    description:
      'Jump on the latest viral trends by swapping faces into popular meme templates, reaction clips, or trending video formats. Create scroll-stopping content that gets likes, shares, and comments across TikTok, Instagram, and YouTube.',
    imageUrl: `${ASSET_PREFIX}social-media-trends-and-memes.webp`,
  },
  {
    id: 'headshots-branding',
    title: 'Professional Headshots & Branding',
    description:
      'Generate polished headshots for LinkedIn profiles, corporate websites, or marketing materials. Swap faces onto professional templates to create consistent, high-quality team photos without scheduling individual photo shoots.',
    imageUrl: `${ASSET_PREFIX}professional-headshots-and-branding.webp`,
  },
  {
    id: 'ecommerce',
    title: 'Virtual Try-Ons for E-Commerce',
    description:
      'Let customers see themselves wearing your products by swapping their faces onto model photos or videos. Boost engagement and reduce returns by offering a virtual try-on experience for fashion, eyewear, and accessories.',
    imageUrl: `${ASSET_PREFIX}virtual-try-ons-for-e-commerce.webp`,
  },
  {
    id: 'storytelling',
    title: 'Creative Storytelling & Film Projects',
    description:
      'Bring characters to life by casting different faces into scenes without reshooting. Ideal for indie filmmakers, content creators, and studios exploring casting options or producing creative visual narratives efficiently.',
    imageUrl: `${ASSET_PREFIX}creative-storytelling-and-film-projects.webp`,
  },
  {
    id: 'friends-family',
    title: 'Friends, Family & Event Fun',
    description:
      'Add a fun twist to birthday videos, holiday greetings, or family memories by swapping faces among friends and relatives. Create memorable, shareable moments that bring laughter to any celebration or gathering.',
    imageUrl: `${ASSET_PREFIX}friends-family-and-event-fun.webp`,
  },
];

interface PossibilityTabProps {
  item: PossibilityItem;
  isActive: boolean;
  onClick: () => void;
}

const PossibilityTab: React.FC<PossibilityTabProps> = ({ item, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#7C3AED] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {item.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {item.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const PossibilitiesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeItem = possibilities[activeIndex];

  const imageUrls = useMemo(() => possibilities.map((p) => p.imageUrl), []);
  useImagePreload(imageUrls);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Discover Endless Possibilities with AI Face Swap
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          From viral social media content and professional branding to e-commerce
          experiences and creative filmmaking, AI face swap opens up a world of
          possibilities for every creator, marketer, and storyteller.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Image */}
          <div className="w-full pad:w-[55%] flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3]">
              <Image
                src={activeItem.imageUrl}
                alt={activeItem.title}
                fill
                className="object-cover transition-opacity duration-300"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-[45%] flex flex-col gap-3 pad:gap-4">
            {possibilities.map((item, index) => (
              <PossibilityTab
                key={item.id}
                item={item}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PossibilitiesSection;
